import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const JAMA_SYNCHRONY_SOURCE = {
  title:
    "Effect of timing by endometrial receptivity testing vs standard timing of frozen embryo transfer on live birth",
  journal: "JAMA",
  year: 2022,
  url: "https://jamanetwork.com/journals/jama/fullarticle/2799154",
  evidenceLevel: "rct" as const,
};

const HFEA_RECEPTIVITY_SOURCE = {
  title: "Endometrial receptivity testing",
  journal: "HFEA treatment add-ons guidance",
  year: 2023,
  url: "https://www.hfea.gov.uk/treatments/treatment-add-ons/endometrial-receptivity-testing/",
  evidenceLevel: "guideline" as const,
};

const NICE_RECEPTIVITY_SOURCE = {
  title:
    "Fertility problems: assessment and treatment, evidence review D, endometrial receptivity testing",
  journal: "NICE NG257",
  year: 2026,
  url: "https://www.nice.org.uk/guidance/ng257/evidence/d-endometrial-receptivity-testing-pdf-15664473329",
  evidenceLevel: "guideline" as const,
};

const ERA_POSITIVE_RCT_SOURCE = {
  title:
    "A 5-year multicentre randomized controlled trial comparing personalized, frozen and fresh blastocyst transfer in IVF",
  journal: "Reproductive BioMedicine Online",
  year: 2020,
  url: "https://pubmed.ncbi.nlm.nih.gov/32723696/",
  evidenceLevel: "rct" as const,
};

const ERA_DIAGNOSTIC_ACCURACY_SOURCE = {
  title:
    "Evaluating the endometrial receptivity assay: a nested diagnostic accuracy study within the Synchrony randomized clinical trial",
  journal: "Fertility and Sterility",
  year: 2023,
  url: "https://pubmed.ncbi.nlm.nih.gov/37714255/",
  evidenceLevel: "observational" as const,
};

const ERPEAK_OUTCOME_SOURCE = {
  title:
    "Clinical relevance of a newly developed endometrial receptivity test for patients with recurrent implantation failure in Japan",
  journal: "Reproductive Medicine and Biology",
  year: 2022,
  url: "https://pubmed.ncbi.nlm.nih.gov/35386362/",
  evidenceLevel: "observational" as const,
};

const BEREADY_MODEL_SOURCE = {
  title:
    "Targeted gene expression profiling for accurate endometrial receptivity testing",
  journal: "Scientific Reports",
  year: 2023,
  url: "https://pubmed.ncbi.nlm.nih.gov/37633957/",
  evidenceLevel: "observational" as const,
};

const IGENOMIX_ERA_PRODUCT_SOURCE = {
  title: "ERA Endometrial Receptivity Analysis",
  journal: "Igenomix product information",
  year: 2026,
  url: "https://www.igenomix.eu/genetic-solutions/era-endometrial-receptivity-analysis/",
  evidenceLevel: "expert-opinion" as const,
};

const IGENOMIX_OFFICES_SOURCE = {
  title: "Igenomix worldwide offices",
  journal: "Igenomix company information",
  year: 2026,
  url: "https://www.igenomix.eu/offices/",
  evidenceLevel: "expert-opinion" as const,
};

const ERPEAK_PRODUCT_SOURCE = {
  title: "ERPeak endometrial receptivity test",
  journal: "CooperSurgical product information",
  year: 2026,
  url: "https://www.coopersurgical.com/product/er-peak/",
  evidenceLevel: "expert-opinion" as const,
};

const ESHRE_ADDONS_SOURCE = {
  title: "Good practice recommendations on add-ons in reproductive medicine",
  authors: "ESHRE Add-ons working group; Lundin K, Bentzen JG, et al.",
  journal: "Human Reproduction",
  year: 2023,
  url: "https://pubmed.ncbi.nlm.nih.gov/37747409/",
  evidenceLevel: "guideline" as const,
};

const BEREADY_PRODUCT_SOURCE = {
  title: "beREADY endometrial receptivity test",
  journal: "beREADY product information",
  year: 2026,
  url: "https://beready.ee/",
  evidenceLevel: "expert-opinion" as const,
};

const BEREADY_PARTNER_CLINIC_SOURCE = {
  title:
    "Comprehensive Endometrial Receptivity Assessment Using the new beREADY Test",
  journal: "OVIklinika patient information",
  year: 2025,
  url: "https://oviklinika.pl/en/beready-test/",
  evidenceLevel: "expert-opinion" as const,
};

const test: ContentBlock<"test"> = {
  id: makeId("test-endometrial-receptivity-testing"),
  kind: "test",
  tags: ["testing", "clinic-visit", "pre-cycle"],
  title: "Endometrial Receptivity Testing",
  subtitle:
    "Biopsy-based transfer-timing add-on with poor routine-use evidence",
  icon: "🔬",
  body: {
    testName: "Endometrial Receptivity Testing",
    category: "biopsy",
    evidenceStrength: "debunked",
    whatItMeasures:
      "Measures endometrial gene expression to classify the uterine lining as receptive, pre-receptive, or post-receptive.\n\nERA is the Igenomix-branded version; ERPeak and beREADY are other branded receptivity tests.\n\nThe result claims to personalize embryo transfer timing, but high-quality evidence does not show live birth benefit for routine use.",
    howItWorks:
      "A receptivity test is done during a mock natural or medicated FET cycle, often at P+5 in a medicated cycle or LH/hCG+7 in a natural cycle. A clinician takes an endometrial biopsy with a thin catheter, the lab analyzes gene expression, and the report may suggest shifting transfer timing. A large randomized trial found no live birth improvement compared with standard timing, and HFEA rates endometrial receptivity testing red for most fertility patients.",
    howItWorksSteps: [
      "Your clinic schedules a mock natural or medicated frozen-transfer cycle rather than testing in the actual transfer cycle.",
      "The biopsy is timed to the expected implantation window, often P+5 in a medicated cycle or LH/hCG+7 in a natural cycle.",
      "A clinician takes a brief endometrial biopsy with a thin catheter.",
      "The lab analyzes gene expression and classifies the sample as receptive, pre-receptive, post-receptive, or inconclusive.",
      "If the report is non-receptive, the clinic may suggest changing progesterone exposure or embryo-transfer timing in a later cycle.",
    ],
    quickFacts: [
      { kind: "sample", value: "Endometrial tissue biopsy" },
      { kind: "time", value: "5-10 min biopsy" },
      { kind: "pain", value: "Cramping during and shortly after" },
      { kind: "anesthesia", value: "Usually none" },
      { kind: "prep", value: "No fasting usually; ask about ibuprofen" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: [
      {
        label: "Receptive",
        meaning:
          "The biopsy timing matches the lab's predicted window of implantation.",
        whatToExpect:
          "Clinics usually keep standard transfer timing. This does not guarantee implantation, and it does not prove that doing receptivity testing improved your odds.",
      },
      {
        label: "Pre-receptive",
        meaning:
          "The lab predicts the biopsy was taken before the endometrium reached its receptive window.",
        whatToExpect:
          "The report may suggest more progesterone exposure before transfer. Large trials have not shown live birth benefit from routine receptivity-timed transfer.",
      },
      {
        label: "Post-receptive",
        meaning:
          "The lab predicts the biopsy was taken after the receptive window had passed.",
        whatToExpect:
          "The report may suggest less progesterone exposure or a shifted transfer time. Ask exactly how much the plan would change and why.",
      },
      {
        label: "Inconclusive",
        meaning: "The sample or timing cannot be interpreted confidently.",
        whatToExpect:
          "A repeat biopsy may be suggested, which can add cost, discomfort, and delay without proven routine benefit.",
      },
    ],
    soldAs: [
      {
        name: "ERA",
        company: "Igenomix",
        description:
          "A branded endometrial receptivity analysis that uses endometrial gene expression to label the sample receptive, pre-receptive, or post-receptive.",
        evidenceSummary:
          "ERA has the most direct clinical-outcome evidence in this category. Results conflict, and the largest blinded trial did not show live birth benefit.",
        linkedTestId: makeId(
          "test-endometrial-receptivity-testing-products-era",
        ),
      },
      {
        name: "ERPeak",
        company: "CooperSurgical",
        description:
          "A branded endometrial receptivity test that uses gene-expression timing to estimate the window of implantation.",
        evidenceSummary:
          "Has a retrospective Japanese RIF study reporting better outcomes, but no large randomized ERPeak trial.",
        linkedTestId: makeId(
          "test-endometrial-receptivity-testing-products-erpeak",
        ),
      },
      {
        name: "beREADY",
        company: "Competence Centre on Health Technologies",
        description:
          "A branded endometrial receptivity test that reports whether the lining appears timed for embryo transfer.",
        evidenceSummary:
          "Has published gene-expression model validation, but no product-specific live birth trial showing benefit.",
        linkedTestId: makeId(
          "test-endometrial-receptivity-testing-products-beready",
        ),
      },
    ],
    expectations: {
      canExpect: [
        "A biopsy report that classifies the sample as receptive, pre-receptive, or post-receptive",
        "A claimed personalized embryo transfer timing based on your specific biology if the lab reports a displaced implantation window",
        "Older branded receptivity-test materials often cite about 25-30% of patients having a displaced window of implantation, but that claim has not translated into live birth benefit in high-quality trials",
        "One mock cycle before the actual transfer cycle",
      ],
      shouldNotExpect: [
        "Do not expect the result to guarantee implantation or explain every failed transfer",
        "Do not expect the result to replace embryo quality, uterine evaluation, transfer technique, or clinical context",
      ],
    },
    considerations: [
      "Ask exactly what result would change your transfer timing before doing the biopsy",
      "Plan for a mock cycle, a brief biopsy, possible cramping, and potential transfer delay",
      "Ask whether an inconclusive or shifted result would require a repeat biopsy",
      "If you have had multiple failed transfers, ask why this test is being recommended for your specific case",
    ],
    bestFor: [
      "Recurrent implantation failure, debated",
      "Not recommended for routine use",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "A randomized trial found no improvement in live birth with receptivity-timed frozen embryo transfer",
        magnitude:
          "Live birth: 58.5% receptivity-timed vs 61.9% standard timing",
        mechanism:
          "Although receptivity testing aims to identify a displaced implantation window, personalized transfer timing based on RNA expression did not improve outcomes in this trial",
        source: "Doyle et al., JAMA 2022 (RCT, n=767)",
      },
      {
        direction: "negative",
        outcome:
          "NICE found no important difference between ERA-guided frozen transfer and standard frozen transfer for live birth or clinical pregnancy",
        source: "NICE NG257 evidence review, 2026",
      },
      {
        direction: "negative",
        outcome:
          "HFEA rates endometrial receptivity testing red for improving the chance of having a baby for most fertility patients",
        source: "HFEA treatment add-ons guidance, 2023",
      },
    ],
    headlineStat: "No live birth benefit",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 800, high: 1200 },
    atHome: false,
    clinicRequired: true,
    cycleDay:
      "Mock FET cycle timing: usually P+5 in a medicated cycle or LH/hCG+7 in a natural cycle.",
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    JAMA_SYNCHRONY_SOURCE,
    HFEA_RECEPTIVITY_SOURCE,
    NICE_RECEPTIVITY_SOURCE,
    ERPEAK_OUTCOME_SOURCE,
    BEREADY_MODEL_SOURCE,
    ERPEAK_PRODUCT_SOURCE,
    BEREADY_PRODUCT_SOURCE,
  ],
  readTimeMinutes: 4,
};

registerBlock(test);

const eraProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-receptivity-testing-products-era"),
  kind: "test",
  tags: ["testing", "clinic-visit", "pre-cycle", "ivf", "fet"],
  title: "ERA",
  subtitle: "Commercial test profile for endometrial receptivity testing",
  icon: "🔬",
  body: {
    testName: "ERA (Endometrial Receptivity Analysis)",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-receptivity-testing"),
      company: "Igenomix",
      productUrl:
        "https://www.igenomix.eu/genetic-solutions/era-endometrial-receptivity-analysis/",
      marketedNames: ["ERA", "ERA®", "Endometrial Receptivity Analysis"],
      availability: [
        {
          region: "Igenomix country pages and offices",
          countries: [
            "US",
            "CA",
            "MX",
            "AR",
            "BR",
            "CO",
            "CL",
            "PE",
            "ES",
            "GB",
            "IT",
            "IN",
            "JP",
            "KR",
            "TW",
            "TR",
            "AE",
          ],
          detail:
            "Igenomix publishes country or office pages for these markets and describes ERA as used by more than 3,000 clinics worldwide. It also uses broader regional navigation for Europe, Latin America, and the Middle East, but exact access still depends on your clinic and local lab route.",
        },
        {
          region: "Patient access",
          detail:
            "Usually ordered by an IVF clinic after a timed mock-cycle endometrial biopsy, not as an at-home direct-to-consumer test.",
        },
      ],
    },
    whatItMeasures:
      "ERA is the Igenomix-branded endometrial receptivity test. It measures endometrial RNA expression after a defined amount of progesterone exposure and classifies the sample as receptive, pre-receptive, post-receptive, or inconclusive.",
    howItWorks:
      "ERA is usually done in a mock medicated or natural frozen-transfer cycle. A clinician takes an endometrial biopsy, the lab analyzes gene expression, and the report may suggest shifting progesterone exposure or embryo-transfer timing. The major evidence caveat is that a large randomized trial did not show improved live birth with receptivity-timed transfer.",
    howItWorksSteps: [
      "Your clinic schedules ERA in a mock natural or medicated frozen-transfer cycle rather than the actual transfer cycle.",
      "The biopsy is timed to the expected implantation window, often P+5 in a medicated cycle or LH/hCG+7 in a natural cycle.",
      "A clinician takes a brief endometrial biopsy with a thin catheter.",
      "Igenomix analyzes endometrial RNA expression and labels the sample receptive, pre-receptive, post-receptive, or inconclusive.",
      "If ERA reports a shifted window, the clinic may suggest changing progesterone exposure or embryo-transfer timing in a later cycle.",
    ],
    quickFacts: [
      { kind: "sample", value: "Endometrial tissue biopsy" },
      { kind: "time", value: "5-10 min biopsy" },
      { kind: "pain", value: "Cramping during and shortly after" },
      { kind: "anesthesia", value: "Usually none" },
      { kind: "prep", value: "No fasting usually; ask about ibuprofen" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: test.body.resultTypes,
    expectations: {
      canExpect: [
        "A branded receptivity report that may recommend standard or adjusted transfer timing",
        "A result that should be interpreted within the broader endometrial receptivity testing evidence base",
      ],
      shouldNotExpect: [
        "Do not expect a receptive result to guarantee implantation",
        "Do not expect ERA to explain every failed transfer",
      ],
    },
    considerations: [
      "Ask why ERA specifically is being recommended instead of standard transfer timing",
      "Ask what your clinic would do with a pre-receptive, post-receptive, or inconclusive result before paying for the test",
      "Ask whether the recommendation is based on repeated failed transfers, clinic routine, or a specific cycle-timing concern",
    ],
    bestFor: test.body.bestFor,
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "The largest blinded randomized trial found no live birth improvement with ERA-style receptivity-timed transfer",
        magnitude:
          "Live birth: 58.5% receptivity-timed vs 61.9% standard timing",
        mechanism:
          "All randomized participants had endometrial receptivity testing, but changing transfer timing from the result did not improve outcomes.",
        source: "Doyle et al., JAMA 2022 (Synchrony RCT, n=767 randomized)",
      },
      {
        direction: "positive",
        outcome:
          "An earlier multicenter ERA RCT reported higher 12-month cumulative live birth with personalized embryo transfer",
        magnitude: "Cumulative live birth: 71.2% PET vs 55.4% frozen ET",
        mechanism:
          "This is the main positive ERA trial, but later blinded evidence did not reproduce the live birth benefit.",
        source: "Simón et al., Reproductive BioMedicine Online 2020",
      },
      {
        direction: "negative",
        outcome:
          "A nested diagnostic accuracy analysis questioned whether a non-receptive ERA result reliably predicts failed implantation",
        mechanism:
          "This matters because the test only helps clinically if the result identifies a timing problem that changes outcome.",
        source: "Chae-Kim et al., Fertility and Sterility 2023",
      },
      {
        direction: "negative",
        outcome:
          "HFEA rates endometrial receptivity testing red for improving the chance of having a baby for most fertility patients",
        source: "HFEA treatment add-ons guidance, 2023",
      },
    ],
    headlineStat: "No live birth benefit",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 800, high: 1500 },
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    JAMA_SYNCHRONY_SOURCE,
    ERA_POSITIVE_RCT_SOURCE,
    ERA_DIAGNOSTIC_ACCURACY_SOURCE,
    HFEA_RECEPTIVITY_SOURCE,
    NICE_RECEPTIVITY_SOURCE,
    IGENOMIX_ERA_PRODUCT_SOURCE,
    IGENOMIX_OFFICES_SOURCE,
  ],
  readTimeMinutes: 4,
};

const erPeakProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-receptivity-testing-products-erpeak"),
  kind: "test",
  tags: ["testing", "clinic-visit", "pre-cycle", "ivf", "fet"],
  title: "ERPeak",
  subtitle: "Commercial test profile for endometrial receptivity testing",
  icon: "🔬",
  body: {
    testName: "ERPeak",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-receptivity-testing"),
      company: "CooperSurgical",
      productUrl: "https://www.coopersurgical.com/product/er-peak/",
      marketedNames: [
        "ERPeak",
        "ERPeak℠",
        "ER-Peak℠ Test",
        "ERPeak endometrial receptivity test",
      ],
      availability: [
        {
          region: "Vendor-published launch region",
          countries: ["US", "CA", "MX"],
          detail:
            "CooperSurgical announced ERPeak ordering in North America first. Treat this as regional availability, not a guarantee that every clinic in each country offers it.",
        },
        {
          region: "International availability",
          detail:
            "CooperSurgical's current product page says ERPeak is available for sale in selected countries around the world, but it does not publish a complete country-by-country list.",
        },
        {
          region: "Public country reference",
          countries: ["AU"],
          detail:
            "An Australian evidence review notes that ERPeak may be available in Australia. Local clinic availability still needs confirmation.",
        },
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic or CooperSurgical genetics workflow after a timed endometrial biopsy.",
        },
      ],
    },
    whatItMeasures:
      "ERPeak is CooperSurgical's branded endometrial receptivity test. Like other receptivity tests, it uses endometrial gene-expression timing to estimate whether the biopsy was taken inside or outside the predicted window of implantation.",
    howItWorks:
      "A clinician takes an endometrial biopsy in a mock transfer cycle and the lab analyzes the sample for receptivity timing. The report may suggest keeping standard timing or shifting embryo transfer timing. The category-level evidence caveat still applies: receptivity-guided transfer has not shown routine live birth benefit.",
    howItWorksSteps: [
      "Your clinic schedules ERPeak in a mock transfer cycle rather than the actual transfer cycle.",
      "The biopsy is timed to the expected implantation window, commonly around P+5 in a medicated cycle or LH/hCG+7 in a natural cycle.",
      "A clinician takes a brief endometrial biopsy with a thin catheter.",
      "CooperSurgical analyzes hormone-regulated gene expression to classify receptivity timing.",
      "If the report suggests pre-receptive or post-receptive timing, the clinic may adjust embryo-transfer timing in a later cycle.",
    ],
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: {
      canExpect: [
        "A branded receptivity timing report from CooperSurgical",
        "A result that should be compared against the broader evidence on receptivity-guided transfer",
      ],
      shouldNotExpect: [
        "Do not expect ERPeak to replace embryo quality, uterine evaluation, or clinical context",
        "Do not expect a timing recommendation to guarantee implantation",
      ],
    },
    considerations: [
      "Ask what direct ERPeak evidence your clinic is relying on, not just evidence for receptivity testing in general",
      "Ask how much the result would change your transfer plan",
      "Ask whether this is bundled with ERBiome or ER-Complete",
    ],
    bestFor: test.body.bestFor,
    keyFindings: [
      {
        direction: "positive",
        outcome:
          "A retrospective Japanese RIF study reported higher clinical pregnancy and live birth after ERPeak-guided personalized transfer",
        magnitude:
          "Clinical pregnancy: 37.7% vs 20.0%; live birth: 29.9% vs 9.7%",
        mechanism:
          "This is product-specific ERPeak outcome evidence, but it was not a randomized trial and was limited to recurrent implantation failure patients.",
        source:
          "Ohara et al., Reproductive Medicine and Biology 2022 (retrospective, n=550)",
      },
      {
        direction: "neutral",
        outcome:
          "ERPeak has product-specific supportive data, but no large randomized ERPeak trial was found",
        mechanism:
          "Use this evidence separately from the broader ERA and receptivity-testing evidence base.",
        source: "Ohara et al., 2022; CooperSurgical product information",
      },
      {
        direction: "negative",
        outcome:
          "Category-level guidance still does not support routine receptivity-guided transfer for most patients",
        source: "HFEA treatment add-ons guidance; NICE NG257 evidence review",
      },
    ],
    headlineStat: "Category not routine",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 800, high: 1500 },
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    ERPEAK_OUTCOME_SOURCE,
    ERPEAK_PRODUCT_SOURCE,
    ESHRE_ADDONS_SOURCE,
    JAMA_SYNCHRONY_SOURCE,
    HFEA_RECEPTIVITY_SOURCE,
    NICE_RECEPTIVITY_SOURCE,
  ],
  readTimeMinutes: 4,
};

const beReadyProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-receptivity-testing-products-beready"),
  kind: "test",
  tags: ["testing", "clinic-visit", "pre-cycle", "ivf", "fet"],
  title: "beREADY",
  subtitle: "Commercial test profile for endometrial receptivity testing",
  icon: "🔬",
  body: {
    testName: "beREADY",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-receptivity-testing"),
      company: "Competence Centre on Health Technologies",
      productUrl: "https://beready.ee/",
      marketedNames: [
        "beREADY",
        "beREADY test",
        "beReady endometrial receptivity test",
      ],
      availability: [
        {
          region: "European Union distribution license",
          countries: [
            "AT",
            "BE",
            "BG",
            "HR",
            "CY",
            "CZ",
            "DK",
            "EE",
            "FI",
            "FR",
            "DE",
            "GR",
            "HU",
            "IE",
            "IT",
            "LV",
            "LT",
            "LU",
            "MT",
            "NL",
            "PL",
            "PT",
            "RO",
            "SK",
            "SI",
            "ES",
            "SE",
          ],
          detail:
            "beREADY states that the test is a CE-IVD medical device licensed to distribute in the European Union. This is a distribution status, not proof that every clinic offers it.",
        },
        {
          region: "Partner clinics",
          countries: ["EE", "PL", "CZ"],
          detail:
            "Public clinic pages show beREADY being offered through clinics in Estonia, Poland, and Czechia; availability depends on the local clinic.",
        },
        {
          region: "Patient access",
          detail:
            "A clinic takes the biopsy and sends the sample to the beREADY laboratory in Estonia; the result returns to the clinic.",
        },
      ],
    },
    whatItMeasures:
      "beREADY is a branded endometrial receptivity test. It belongs to the same broad category as ERA and ERPeak: biopsy-based tests that try to estimate whether the lining is timed for embryo transfer.",
    howItWorks:
      "A clinician takes an endometrial biopsy in a timed cycle and the lab analyzes molecular signals related to receptivity. The report may classify timing and suggest whether transfer timing should be adjusted. The broader evidence caveat remains that routine receptivity-guided transfer is not proven to improve live birth.",
    howItWorksSteps: [
      "Your clinic schedules beREADY in a timed mock cycle rather than the actual transfer cycle.",
      "The biopsy is timed to the expected implantation window, commonly around P+5 in a medicated cycle or LH/hCG+7 in a natural cycle.",
      "A clinician takes a brief endometrial biopsy with a thin catheter.",
      "The lab uses targeted gene-expression profiling to estimate whether the lining is timed for transfer.",
      "If the report suggests shifted timing, the clinic may discuss whether to adjust embryo-transfer timing in a later cycle.",
    ],
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: {
      canExpect: [
        "A branded receptivity report that should be interpreted as part of the broader receptivity-testing category",
        "A clinic discussion about whether the result changes transfer timing",
      ],
      shouldNotExpect: [
        "Do not expect beREADY to guarantee implantation or live birth",
        "Do not expect beREADY to replace embryo quality, uterine evaluation, or clinical context",
      ],
    },
    considerations: [
      "Ask what evidence is specific to beREADY versus receptivity testing generally",
      "Ask what result would change your plan and by how much",
      "Ask whether a repeat biopsy would be required if the result is inconclusive",
    ],
    bestFor: test.body.bestFor,
    keyFindings: [
      {
        direction: "neutral",
        outcome:
          "Published beREADY evidence supports the gene-expression model, not improved live birth from using the test",
        magnitude:
          "The model uses 72 genes and TAC-seq targeted expression profiling",
        mechanism:
          "This is analytical and diagnostic validation evidence. It does not prove that beREADY-guided transfer improves fertility outcomes.",
        source: "Meltsov et al., Scientific Reports 2023",
      },
      {
        direction: "neutral",
        outcome:
          "Company material reports high same-sample agreement with ERA, but concordance with another test is not clinical benefit",
        magnitude: "96% concordance in 45 HRT samples, reported by beREADY",
        mechanism:
          "Agreement with ERA may show similar classification, but ERA itself has conflicting outcome evidence.",
        source: "beREADY product information",
      },
      {
        direction: "negative",
        outcome:
          "No product-specific randomized live birth trial for beREADY was found",
        source: "Category-level HFEA and NICE receptivity-testing guidance",
      },
    ],
    headlineStat: "Category not routine",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 800, high: 1500 },
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    BEREADY_MODEL_SOURCE,
    BEREADY_PRODUCT_SOURCE,
    BEREADY_PARTNER_CLINIC_SOURCE,
    JAMA_SYNCHRONY_SOURCE,
    HFEA_RECEPTIVITY_SOURCE,
    NICE_RECEPTIVITY_SOURCE,
  ],
  readTimeMinutes: 4,
};

registerBlock(eraProduct);
registerBlock(erPeakProduct);
registerBlock(beReadyProduct);

for (const productId of [
  "test-endometrial-receptivity-testing-products-era",
  "test-endometrial-receptivity-testing-products-erpeak",
  "test-endometrial-receptivity-testing-products-beready",
]) {
  registerRelationship({
    from: makeId("test-endometrial-receptivity-testing"),
    to: makeId(productId),
    type: "related-to",
    label: "Sold as",
  });
}
